import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import type { MatchedBudget } from "@/components/new-project/PdfOfferUpload";
import type { MatchedBudgetLine } from "@/lib/match-offer-line-templates";
import type { OfferPhase } from "@/lib/offer-calc";
import type { OfferPricingType } from "./use-offers-queries";
import { useCreateOffer, useUpdateOffer } from "./use-offer-mutations";

/**
 * VIB-1130: Opprett et tilbud direkte fra et Vision-importert budsjett.
 *
 * Speiler MatchedBudget→budget_lines-mappingen i NewProjectWizard
 * (`use-new-project-form.ts:applyOffer` + `NewProjectWizard.handleCreate`),
 * men skriver til `offer_lines` i stedet for et prosjekts `budget_lines`.
 * Offer-metadata (overhead/rabatt/faktureringsterminer) lever som kolonner
 * på `offers`-raden — overhead settes ved create, rabatt + terminer
 * etterpå via useUpdateOffer.
 */

/** Insert-shape for offer_lines (mirror insertTemplateLinesForOffer). */
export interface OfferLineInsertRow {
  offer_id: string;
  template_id: string | null;
  phase: OfferPhase;
  description: string;
  cost_type: string;
  pricing_type: OfferPricingType;
  quantity: number;
  unit: string;
  unit_price: number;
  amount: number;
  sort_order: number;
  /** Tilvalg-seksjon linjen tilhører; null = hovedbudsjettet. */
  section_id: string | null;
}

/**
 * Mapper Vision-matchede budsjettlinjer → offer_lines-rader.
 *
 * - `template_id` bevares fra mal-match (null hvis ingen match).
 * - `phase`/`cost_type` bruker de effektive (mal-styrte) verdiene, med
 *   has_time-fallback for umatchede linjer (samme som applyOffer).
 * - `pricing_type` hentes fra malen, ellers utledes fra has_time.
 * - `amount` er kilde-sannheten; `unit_price` utledes av amount/quantity
 *   slik at editor-summer stemmer uten å vise feil enhetspris.
 */
export function mapBudgetToOfferLines(
  offerId: string,
  lines: MatchedBudgetLine[],
  sectionId: string | null = null,
): OfferLineInsertRow[] {
  return lines.map((line, idx) => {
    const match = line.template_match;
    const cost_type = line.effective_cost_type;
    const pricing_type: OfferPricingType =
      (match?.pricing_type as OfferPricingType | undefined) ??
      (line.has_time ? "hourly" : "fixed");
    const quantity = line.quantity ?? 0;
    const unit = line.unit ?? match?.default_unit ?? (line.has_time ? "t" : "stk");
    const amount = line.amount ?? 0;
    const unit_price = quantity > 0 ? Math.round(amount / quantity) : 0;
    return {
      offer_id: offerId,
      template_id: match?.template_id ?? null,
      phase: line.effective_phase,
      description: line.description,
      cost_type,
      pricing_type,
      quantity,
      unit,
      unit_price,
      amount,
      sort_order: idx * 10,
      section_id: sectionId,
    };
  });
}

export interface CreateOfferFromImportInput {
  customerId: string;
  customerName: string | null;
  budget: MatchedBudget;
}

export function useCreateOfferFromImport() {
  const queryClient = useQueryClient();
  const createOffer = useCreateOffer();
  const updateOffer = useUpdateOffer();

  return useMutation({
    mutationFn: async ({
      customerId,
      customerName,
      budget,
    }: CreateOfferFromImportInput): Promise<string> => {
      // VIB-686: oversett invoice_split til to-rad terminliste (som applyOffer).
      const startupPct = budget.invoice_split?.startup_pct;
      const invoice_terms =
        startupPct != null && startupPct > 0
          ? [
              { label: "Oppstart", pct: startupPct },
              { label: "Levering", pct: 100 - startupPct },
            ]
          : undefined;

      const offer = await createOffer.mutateAsync({
        title: budget.name?.trim() || "Importert tilbud",
        customer_id: customerId,
        customer_name: customerName,
        // Importerte budsjetter har egen overhead fra Vision; bær den over
        // i stedet for system-default.
        overhead_pct: budget.overhead_pct ?? undefined,
        ...(invoice_terms ? { invoice_terms } : {}),
        source: "import", // VIB-1136: importerte tilbud får ingen auto-linjer
      });

      // Persister rabatt + faktureringsterminer på tilbudsraden. overhead
      // er allerede satt ved create.
      const discountPatch: Record<string, number> = {};
      if (budget.discount_pct) discountPatch.discount_pct = budget.discount_pct;
      if (budget.discount_amount)
        discountPatch.discount_amount = budget.discount_amount;
      if (startupPct != null && startupPct > 0) {
        discountPatch.startup_invoice_pct = startupPct;
        discountPatch.final_invoice_pct = 100 - startupPct;
      }
      if (Object.keys(discountPatch).length > 0) {
        await updateOffer.mutateAsync({ id: offer.id, patch: discountPatch });
      }

      // VIB-1142: hvert tilvalg-budsjett blir sin egen navngitte option-seksjon
      // (ikke samlet til én «Tilvalg»). OfferPicker stempler option_group ved
      // kombinering; for enkelt-importer faller vi tilbake på budsjettnavnet.
      const isLineOptional = (l: MatchedBudgetLine) =>
        l.is_optional ?? budget.is_optional ?? false;
      const mainLines = budget.lines.filter((l) => !isLineOptional(l));
      const optionLines = budget.lines.filter(isLineOptional);

      const mainRows = mapBudgetToOfferLines(offer.id, mainLines, null);
      if (mainRows.length > 0) {
        const { error } = await supabase.from("offer_lines").insert(mainRows);
        if (error) throw error;
      }

      // Gruppér tilvalg-linjer per kilde (stabil innsettingsrekkefølge). Tom
      // gruppenøkkel → nummerert «Tilvalg N»-fallback.
      // Ledende mellomrom: kan aldri kollidere med en trimmet option_group/navn.
      const NONAME = " noname";
      const groups = new Map<string, MatchedBudgetLine[]>();
      for (const l of optionLines) {
        const key = l.option_group?.trim() || budget.name?.trim() || NONAME;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(l);
      }

      let groupIdx = 0;
      for (const [key, groupLines] of groups) {
        const name = key === NONAME ? `Tilvalg ${groupIdx + 1}` : key;
        const { data: section, error: sectionErr } = await supabase
          .from("offer_sections")
          .insert({
            offer_id: offer.id,
            name,
            section_type: "option",
            sort_order: groupIdx + 1,
          })
          .select("id")
          .single();
        if (sectionErr) throw sectionErr;
        const optionRows = mapBudgetToOfferLines(offer.id, groupLines, section.id);
        const { error } = await supabase.from("offer_lines").insert(optionRows);
        if (error) throw error;
        groupIdx++;
      }

      return offer.id;
    },
    onSettled: async (offerId) => {
      await invalidateFor(queryClient, "offers", { offerId });
      await invalidateFor(queryClient, "offer_lines", { offerId });
      await invalidateFor(queryClient, "offer_sections", { offerId });
    },
  });
}
